import React, { useEffect, useRef, useCallback } from "react";
import Header from "../components/Header";
import Cart from "../components/Cart";
import AboutPage from "./AboutPage";
import HomePage from "./HomePage";
import {
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { trackPageView } from "../lib/analytics";
import { trackPixelPageView } from "../lib/meta-pixel";
import ProductLinePage from "./ProductLinePage";
import { useSearch } from "../contexts/SearchContext";
import ProductPage from "./ProductPage";
import { useCart } from "../contexts/CartContext";
import HamburgerMenu from "../components/HamburgerMenu";
import { useHamburgerMenu } from "../contexts/HamburgerMenuContext";
import SearchMenu from "../components/SearchMenu";
import PaymentReceiptPage from "./PaymentReceiptPage";
import PaymentFormPage from "./PaymentFormPage";
import PodcastPage from "./PodcastPage";
import OrderCancelPage from "./OrderCancelPage";
import OrderTrackPage from "./OrderTrackPage";
import {
  FeatureToggle,
  useFeatureToggle,
} from "../contexts/FeatureToggleContext";
import OrderReturnPage from "./OrderReturnPage";
import AccountLoginPage from "./AccountLoginPage";
import AccountPage from "./AccountPage";
import AccountOrderDetailPage from "./AccountOrderDetailPage";
import AdminPage from "./AdminPage";
import ContactPage from "./ContactPage";
import WishlistPage from "./WishlistPage";
import America250Banner from "../components/America250Banner";
import AnnouncementBar from "../components/AnnouncementBar";
import ErrorBoundary from "../components/ErrorBoundary";
import NotFoundPage from "./NotFoundPage";
import PolicyPage from "./PolicyPage";
import SizeGuidePage from "./SizeGuidePage";
import FaqPage from "./FaqPage";

const MainPage: React.FC = () => {
  const { isFeatureEnabled, setFeatureEnabled } = useFeatureToggle();

  const { isCartOpen, setIsCartOpen } = useCart();
  const { isHamburgerMenuOpen, setIsHamburgerMenuOpen } = useHamburgerMenu();
  const { isSearchMenuOpen, setIsSearchMenuOpen } = useSearch();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // SPA page-view tracking (no-op unless analytics/pixel are configured and the
  // visitor has accepted — both helpers self-gate)
  useEffect(() => {
    trackPageView(location.pathname);
    trackPixelPageView();
  }, [location.pathname]);

  const cartRef = useRef<HTMLDivElement>(null);
  const hamburgerMenuRef = useRef<HTMLDivElement>(null);
  const searchMenuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        setIsCartOpen(false);
      }
      if (
        hamburgerMenuRef.current &&
        !hamburgerMenuRef.current.contains(event.target as Node)
      ) {
        setIsHamburgerMenuOpen(false);
      }
      if (
        searchMenuRef.current &&
        !searchMenuRef.current.contains(event.target as Node)
      ) {
        setIsSearchMenuOpen(false);
      }
    },
    [setIsCartOpen, setIsHamburgerMenuOpen, setIsSearchMenuOpen],
  );

  useEffect(() => {
    if (isCartOpen || isHamburgerMenuOpen || isSearchMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside, isCartOpen, isHamburgerMenuOpen, isSearchMenuOpen]);

  useEffect(() => {
    if (searchParams.get("paymentprocess") === "enabled") {
      setFeatureEnabled(FeatureToggle.PaymentProcess);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // prettier-ignore
  return (
    <>
      {/* Skip link (WCAG 2.4.1): first tab stop, jumps past the nav to content. */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <div className={`app-container ${isCartOpen && "opacity-75"}`}>
        <America250Banner />
        <AnnouncementBar />
        <Header />
        <ErrorBoundary>
        <main id="main-content" tabIndex={-1}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/home" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/podcast" element={<PodcastPage />} />

          {/* Clothing */}
          <Route path="/shop/:domain/category/:category/product/:product" element={<ProductPage />} />
          <Route path="/shop/:domain/category/:category" element={<ProductLinePage />} />
          <Route path="/shop/:domain" element={<ProductLinePage />} />

          {/* Wishlist */}
          <Route path="/wishlist" element={<WishlistPage />} />

          {isFeatureEnabled(FeatureToggle.PaymentProcess) &&
            <Route element={<ErrorBoundary><Outlet /></ErrorBoundary>}>
              {/* Payment Checkout */}
              <Route path="/payment/checkout" element={<PaymentFormPage />} />
              <Route path="/payment/receipt" element={<PaymentReceiptPage />} />

              {/* Orders */}
              <Route path="/orders/track" element={<OrderTrackPage />} />
              <Route path="/orders/cancel" element={<OrderCancelPage />} />
              <Route path="/orders/return" element={<OrderReturnPage />} />
            </Route>
          }

          {/* Account */}
          <Route path="/account/login" element={<AccountLoginPage />} />
          <Route path="/account/orders/:orderId" element={<AccountOrderDetailPage />} />
          <Route path="/account" element={<AccountPage />} />

          {/* Admin portal — self-guards on the admin allowlist; every admin
              endpoint it calls re-verifies the JWT server-side. */}
          <Route path="/admin" element={<AdminPage />} />

          {/* Policies */}
          <Route path="/terms" element={<PolicyPage policy="terms" />} />
          <Route path="/privacy" element={<PolicyPage policy="privacy" />} />
          <Route path="/returns" element={<PolicyPage policy="returns" />} />
          <Route path="/shipping" element={<PolicyPage policy="shipping" />} />
          <Route path="/return-policy" element={<Navigate to="/returns" replace />} />
          <Route path="/size-guide" element={<SizeGuidePage />} />
          <Route path="/faq" element={<FaqPage />} />

          {/* Catch-all 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </main>
        </ErrorBoundary>
      </div>

      {/* Side Menus */}
      {/* These wrappers exist only to carry the click-outside refs — the
          drawers position and style themselves (.cart / .search-menu). The
          cart's wrapper used to set its own width, padding and background,
          which left a 320x32 cream box sitting behind a 416px drawer. */}
      {isCartOpen && (
        <div ref={cartRef}>
          <Cart />
        </div>
      )}

      {isHamburgerMenuOpen && (
        <div ref={hamburgerMenuRef}>
          <HamburgerMenu />
        </div>
      )}

      {isSearchMenuOpen && (
        <div ref={searchMenuRef}>
          <SearchMenu />
        </div>
      )}
    </>
  );
};

export default MainPage;
